import { useMutation } from '@apollo/client';
import { Button, Col, Divider, Form, Input, Modal, Row, Switch } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { filter, includes, keys, map } from 'lodash';
import { evaluate } from 'mathjs';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../../../AppContext';
import { InfoIconDark } from '../../../../../../assets/svg';
import {
  ALLOWED_ACTION_KEYS,
  ALLOWED_ACTION_TYPE,
  BREAKPOINTS,
  FIELD_TYPES,
  FIELD_TYPES_LABELS,
  MATHEMATICAL_FUNCTIONS,
  OPERATORS,
  OPERATORS_WITHOUT_BRACKETS,
  REGEX,
} from '../../../../../../common/constants';
import useRouter from '../../../../../../common/useRouter';
import { formValidatorRules, titleCase } from '../../../../../../common/utils';
import CanPerform from '../../../../../../components/CanPerform';
import CommonPopover from '../../../../../../components/CommonPopover';
import CommonSelect from '../../../../../../components/CommonSelect';
import RouterPrompt from '../../../../../../components/RouterPrompt';
import {
  CREATE_REGISTER_FORM_FIELD,
  UPDATE_REGISTER_FORM_FIELD,
} from '../../../../graphql/Mutation';
import CommonRegisterOptionUi from './CommonRegisterOptionUi';

const { required, characterWithoutWhiteSpace } = formValidatorRules;

const CalculationLogicUi = ({ isDisabled, numericFields, form, inputRef }) => {
  const handleButtonClick = (field) => {
    const fieldTitle = field?.name;
    const currentFormula = form.getFieldValue('formula') || '';
    form?.setFieldsValue({
      formula: `${currentFormula}"${fieldTitle}"`,
    });
    inputRef?.current?.focus();
  };

  const handleOperatorClick = (operator) => {
    const currentFormula = form.getFieldValue('formula') || '';
    let newOperator = operator;

    if (OPERATORS_WITHOUT_BRACKETS?.includes(operator)) {
      newOperator = ` ${operator} `;
    }

    form?.setFieldsValue({ formula: currentFormula + newOperator });
    inputRef?.current?.focus();
  };

  return (
    <>
      <Row gutter={[20, 20]}>
        <Col span={24}>
          <Form.Item
            name="formula"
            label="Formula"
            className="numeric-input"
            rules={[
              () => ({
                validator(_, value) {
                  if (!value) {
                    return Promise.reject(new Error('This field is required!'));
                  }
                  if (value?.length > 1000) {
                    return Promise.reject(
                      new Error('Formula cannot be more than 1000 characters'),
                    );
                  }
                  try {
                    const formulaWithTestValues = value.replace(
                      /"[^"]+"/g,
                      '1',
                    );
                    evaluate(formulaWithTestValues);
                    return Promise.resolve();
                  } catch (error) {
                    return Promise.reject(
                      new Error('Please enter a valid formula'),
                    );
                  }
                },
              }),
            ]}
          >
            <Input
              ref={inputRef}
              placeholder="Enter Value"
              allowClear
              disabled={numericFields?.length === 0 || isDisabled}
            />
          </Form.Item>
          <div className="note">
            {numericFields?.length > 0
              ? "Enter valid formula using field names (Eg. 'field 1' + 'field 2')"
              : 'Add at least one required numeric field to enable calculations'}
          </div>
          <h4 className="mb-10">Fields</h4>
          {numericFields?.length > 0 ? (
            <div className="d-flex mb-10 flex-wrap">
              {numericFields?.map((field) => (
                <>
                  <div key={field?.id} className="d-flex flex-wrap">
                    <Button
                      shape="round"
                      className="cancel-button mr-10 mb-10"
                      disabled={isDisabled}
                      onClick={() => handleButtonClick(field)}
                    >
                      {field?.name}
                    </Button>
                  </div>
                </>
              ))}
            </div>
          ) : (
            <div className="text-danger note-text mt-10 mb-10">
              Note: Please add required numeric fields to set the formula.
            </div>
          )}
          <h4>Operators</h4>
          <div className="d-flex mb-10">
            {map(OPERATORS, (operator) => (
              <Button
                key={operator}
                shape="round"
                className="d-flex align-center save-button mr-10"
                onClick={() => handleOperatorClick(operator)}
                disabled={numericFields?.length === 0 || isDisabled}
              >
                {operator}
              </Button>
            ))}
          </div>
          <h4 className="d-flex align-center">
            Mathematical Functions
            <CommonPopover
              title="Mathematical Functions Help"
              overlayClassName="math-function-popover"
              content={
                <div>
                  <div>
                    Mathematical functions are used to perform mathematical
                    operations on the fields. Here are some common mathematical
                    functions available:
                  </div>
                  <ul>
                    <li>
                      <b>mean("a", "b", ...):</b> Calculates the average.
                    </li>
                    <li>
                      <b>median("a", "b", ...):</b> Finds the middle value.
                    </li>
                    <li>
                      <b>std("a", "b", ...):</b> Computes the standard
                      deviation.
                    </li>
                  </ul>
                </div>
              }
            >
              <InfoIconDark
                height={16}
                width={16}
                className="ml-5 rotate-180"
              />
            </CommonPopover>
          </h4>
          <div className="d-flex mb-10">
            {map(MATHEMATICAL_FUNCTIONS, (operator) => (
              <Button
                key={operator}
                shape="round"
                className="d-flex align-center save-button mr-10"
                onClick={() => handleOperatorClick(operator)}
                disabled={numericFields?.length === 0 || isDisabled}
              >
                {titleCase(operator)}
              </Button>
            ))}
          </div>
        </Col>
      </Row>
    </>
  );
};

const AddFieldModal = ({
  showModal,
  setShowModal,
  numericFields,
  setNumericFields,
  tableHeaders,
  isDisabled = false,
  selectedField,
  isUpdate,
  setSelectedField,
  handleRefetch,
}) => {
  const {
    params: { registerId },
  } = useRouter();
  const { state, dispatch } = useContext(AppContext);
  const inputRef = useRef(null);
  const [form] = Form.useForm();
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const [displayOption, setDisplayOption] = useState(false);
  const [isOptionType, setIsOptionType] = useState(false);
  const [displayOptionValue, setDisplayOptionValue] = useState();
  const optionsInputRef = useRef(null);

  const showRouterPrompt = (value) => {
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: value,
    });
  };

  useEffect(() => {
    if (optionsInputRef?.current) {
      optionsInputRef?.current?.focus();
    }
  }, [displayOptionValue]);

  useEffect(() => {
    let filteredFields = [];
    if (selectedField?.id) {
      filteredFields = tableHeaders?.filter(
        (field) =>
          includes(
            [FIELD_TYPES.CALCULATION, FIELD_TYPES.NUMERIC],
            field?.type,
          ) && field?.id !== selectedField?.id,
      );
      setNumericFields(filteredFields);
    } else {
      filteredFields = tableHeaders?.filter((field) =>
        includes([FIELD_TYPES.CALCULATION, FIELD_TYPES.NUMERIC], field?.type),
      );
    }
    setNumericFields(filteredFields);
  }, [tableHeaders]);

  const selectionType = useWatch('type', form);
  const [createRegisterFormField, { loading: createLoading }] = useMutation(
    CREATE_REGISTER_FORM_FIELD,
    {
      onError() {},
      onCompleted: () => {
        setShowModal(false);
        handleRefetch();
      },
    },
  );
  const [updateRegisterFormField, { loading: updateLoading }] = useMutation(
    UPDATE_REGISTER_FORM_FIELD,
    {
      onError() {},
      onCompleted: () => {
        setShowModal(false);
        handleRefetch();
      },
    },
  );

  const onFinish = () => {
    const formValues = form.getFieldsValue();

    if (formValues?.type === FIELD_TYPES.CALCULATION) {
      const optionArr = [];
      if (registerId) {
        const formattedFormula = formValues?.formula?.replace(
          /"([^"]+)"/g,
          (match) => {
            return filter(
              numericFields,
              (record) => match === `"${record?.name}"`,
            )?.[0]?.id;
          },
        );
        formValues.options = [formattedFormula];
        const formulaValue = formattedFormula
          ?.replace(/\\"/g, '')
          ?.replace(/"/g, '');
        optionArr?.push(formulaValue);
      } else {
        formValues.options = [formValues?.formula];
        const formulaValue = formValues?.formula
          ?.replace(/\\"/g, '')
          ?.replace(/"/g, '');
        optionArr?.push(formulaValue);
      }
      formValues.options = optionArr;
      delete formValues?.formula;
    }
    if (
      formValues?.options?.length > 0 &&
      formValues?.type !== FIELD_TYPES.CALCULATION
    ) {
      formValues.options = formValues?.options?.map((value) => value?.option);
    }
    if (isUpdate) {
      updateRegisterFormField({
        variables: {
          updateRegisterFormFieldId: selectedField?.id,
          data: {
            name: formValues?.name,
            isRequired: formValues?.isRequired,
            type: formValues?.type,
            options: formValues?.options || null,
            sequence: selectedField?.sequence,
          },
        },
      });
      showRouterPrompt(false);
    } else {
      createRegisterFormField({
        variables: {
          data: {
            registerId,
            ...formValues,
            options: formValues?.options || null,
            sequence:
              tableHeaders?.[tableHeaders?.length - 1]?.sequence + 1 || 1,
          },
        },
      });
      showRouterPrompt(false);
    }
  };

  const handleTypeChange = (options) => {
    switch (options?.value) {
      case FIELD_TYPES.OPTIONS:
        setDisplayOption(true);
        setIsOptionType(true);
        break;

      case FIELD_TYPES.CHECKBOXES:
        setDisplayOption(true);
        setIsOptionType(true);
        break;

      case FIELD_TYPES.CALCULATION:
        setDisplayOption(true);
        setIsOptionType(false);
        setDisplayOptionValue(
          CalculationLogicUi({
            isDesktopViewport,
            isDisabled,
            numericFields,
            form,
            inputRef,
          }),
        );
        break;

      default:
        setDisplayOption(false);
        setIsOptionType(false);
        break;
    }
  };

  useEffect(() => {
    if (isUpdate && selectedField) {
      handleTypeChange({ value: selectedField?.type });
    }
  }, [showModal, selectedField, numericFields]);

  const handleSelect = (labeledValue, options) => {
    handleTypeChange(options);
    const existingOptions = form?.getFieldValue('options');
    if (labeledValue === FIELD_TYPES.OPTIONS) {
      form.setFieldsValue({
        options: existingOptions?.length ? existingOptions : [{}, {}],
      });
    } else if (labeledValue === FIELD_TYPES.CHECKBOXES) {
      form.setFieldsValue({
        options: existingOptions?.length ? existingOptions : [{}, {}],
      });
    }
  };

  useEffect(() => {
    if (isUpdate && showModal && selectedField) {
      const formattedOptions =
        selectedField?.type !== FIELD_TYPES.CALCULATION
          ? selectedField?.options?.map((option) => ({
              option,
            }))
          : [];
      if (selectedField?.type === FIELD_TYPES.CALCULATION) {
        const formattedFormula =
          selectedField?.type === FIELD_TYPES.CALCULATION
            ? selectedField?.options[0]
            : [];
        const replacedFormula = formattedFormula?.replace(
          REGEX.UUID,
          (match) => {
            return `"${
              filter(numericFields, (record) => match === record?.id)?.[0]?.name
            }"`;
          },
        );
        form.setFieldsValue({ formula: replacedFormula });
      }

      const transformedValues = {
        ...selectedField,
        options: selectedField?.options?.map((option) => ({ option })) || null,
      };
      form.setFieldsValue({
        ...selectedField,
        options: formattedOptions,
      });
      form.setFieldsValue(transformedValues);
    }
  }, [showModal, selectedField, isUpdate, numericFields]);

  return (
    <div>
      <RouterPrompt openPrompt={state?.showPrompt} />
      <Modal
        open={showModal}
        footer={false}
        centered
        className="register-table-modal"
        maskClosable={false}
        closable={false}
        form={form}
        destroyOnClose
      >
        <h2 className="mb-15">{isUpdate ? 'Edit ' : 'Add '} Field</h2>
        <Form
          layout="vertical"
          form={form}
          initialValues={{
            type: 'TEXT',
            isRequired: true,
            options: [{}, {}],
          }}
          onFieldsChange={() => {
            showRouterPrompt(true);
          }}
          className="mt-16"
          onFinish={onFinish}
        >
          <Form.Item
            rules={[
              required,
              characterWithoutWhiteSpace,
              { max: 250, message: 'Maximum 250 characters are allowed' },
            ]}
            name="name"
            label="Property Name"
          >
            <Input
              placeholder="Enter property name"
              disabled={isDisabled}
              allowClear
            />
          </Form.Item>
          <Row gutter={30}>
            <Col span={isDesktopViewport ? 18 : 24}>
              <Form.Item name="type" label="Type">
                <CommonSelect
                  className="mr-3"
                  placeholder="Select Type"
                  onSelect={handleSelect}
                  options={[
                    ...keys(FIELD_TYPES).map((type) => {
                      return {
                        key: FIELD_TYPES_LABELS[type],
                        value: type,
                        label: FIELD_TYPES_LABELS[type],
                      };
                    }),
                  ]}
                  disabled={isDisabled}
                />
              </Form.Item>
            </Col>
            <Col span={isDesktopViewport ? 6 : 24}>
              <Form.Item
                name="isRequired"
                label="Required"
                valuePropName="checked"
              >
                <Switch defaultChecked disabled={isDisabled} />
              </Form.Item>
            </Col>
          </Row>
          {selectionType === FIELD_TYPES?.CALCULATION && (
            <div className="text-danger note-text">
              Note: All related field will be made mandatory
            </div>
          )}
          {displayOption && (
            <>
              <Divider dashed />
              {isOptionType ? (
                <>
                  <h3>Options</h3>
                  <CommonRegisterOptionUi
                    isDisabled={isDisabled}
                    form={form}
                    optionsInputRef={optionsInputRef}
                  />
                </>
              ) : (
                <h3>Calculation</h3>
              )}

              <div className="mt-10">{displayOptionValue}</div>
            </>
          )}
          <div className="form-buttons">
            <Button
              shape="round"
              className="cancel-button"
              disabled={createLoading || updateLoading}
              onClick={() => {
                setShowModal(false);
                setSelectedField();
                showRouterPrompt(false);
              }}
            >
              Cancel
            </Button>
            <CanPerform
              action={ALLOWED_ACTION_KEYS.ADD_PROJECT_REGISTER}
              type={ALLOWED_ACTION_TYPE.BOTH}
            >
              {!isDisabled && (
                <Button
                  shape="round"
                  type="primary"
                  className="save-button"
                  htmlType="submit"
                  loading={createLoading || updateLoading}
                >
                  Save
                </Button>
              )}
            </CanPerform>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AddFieldModal;
